import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import ContactPage from './components/ContactPage';
import GalleryPage from './components/GalleryPage';
import BlogPage from './components/BlogPage';
import ServicesPage from './components/ServicesPage';
import AboutPage from './components/AboutPage';
import ProjectsPage from './components/ProjectsPage';
import TemplateLeftSidebar from './components/TemplateLeftSidebar';
import TemplateRightSidebar from './components/TemplateRightSidebar';
import TemplateFullWidth from './components/TemplateFullWidth';
import TemplateNoSidebar from './components/TemplateNoSidebar';
import TemplateGridLayout from './components/TemplateGridLayout';

// Global Header Component with proper active state management
function GlobalHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeHomeSection, setActiveHomeSection] = useState('home');
  const [pendingScrollSection, setPendingScrollSection] = useState(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Handle resize to close mobile menu on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Listen for scroll to update active section on homepage
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const sections = ['home', 'about', 'services', 'coaches', 'results', 'news'];
        const scrollPosition = window.scrollY + 100;

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i]);
          if (section && section.offsetTop <= scrollPosition) {
            setActiveHomeSection(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Handle pending scroll section after navigation
  useEffect(() => {
    if (location.pathname === '/' && pendingScrollSection) {
      const element = document.getElementById(pendingScrollSection);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          setActiveHomeSection(pendingScrollSection);
          setPendingScrollSection(null);
        }, 100);
      }
    }
  }, [location.pathname, pendingScrollSection]);
  
  const isActive = (path, section = null) => {
    // For other pages (contact, gallery, blog)
    if (path !== '/') {
      return location.pathname === path;
    }
    
    // For homepage sections
    if (path === '/' && location.pathname === '/') {
      if (section) {
        return activeHomeSection === section;
      }
      return activeHomeSection === 'home';
    }
    
    return false;
  };

  const handleHomeNavClick = (e, sectionId) => {
    e.preventDefault();
    closeMobileMenu();
    
    // If HOME button is clicked, always navigate to home page
    if (sectionId === 'home') {
      navigate('/');
      return;
    }
    
    // For other sections (about, services, coaches)
    if (location.pathname === '/') {
      // If already on home page, scroll to section
      setActiveHomeSection(sectionId);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      // If not on home page, navigate to home page and set pending scroll
      setPendingScrollSection(sectionId);
      navigate('/');
    }
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <img src="/assets/main logo.png" alt="Sports Alliance Logo" className="logo-icon" />
            <span className="logo-text">ASKME SPORTS ALLIANCE</span>
          </div>
          <nav className={`navigation ${isMobileMenuOpen ? 'active' : ''}`}>
            <Link to="/" className={`nav-link ${isActive('/', 'home') ? 'active' : ''}`} onClick={(e) => handleHomeNavClick(e, 'home')}>HOME</Link>
            <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`} onClick={closeMobileMenu}>ABOUT</Link>
            <Link to="/services" className={`nav-link ${isActive('/services') ? 'active' : ''}`} onClick={closeMobileMenu}>SERVICES</Link>
            <Link to="/projects" className={`nav-link ${isActive('/projects') ? 'active' : ''}`} onClick={closeMobileMenu}>PROJECTS</Link>
            <Link to="/" className={`nav-link ${isActive('/', 'coaches') ? 'active' : ''}`} onClick={(e) => handleHomeNavClick(e, 'coaches')}>COACHES</Link>
            <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`} onClick={closeMobileMenu}>CONTACT US</Link>
            <Link to="/gallery" className={`nav-link ${isActive('/gallery') ? 'active' : ''}`} onClick={closeMobileMenu}>GALLERY</Link>
            <div className="nav-dropdown">
              <Link to="/blog" className={`nav-link ${isActive('/blog') ? 'active' : ''}`} onClick={closeMobileMenu}>BLOG</Link>
          <div className="dropdown-menu">
            <Link to="/blog" className="dropdown-item">BLOG LEFT SIDEBAR</Link>
            <Link to="/blog" className="dropdown-item">BLOG RIGHT SIDEBAR</Link>
            <Link to="/blog" className="dropdown-item">BLOG FULL WIDTH</Link>
            <Link to="/blog" className="dropdown-item">BLOG NO SIDEBAR</Link>
            <Link to="/blog" className="dropdown-item">BLOG GRID LAYOUT</Link>
          </div>
            </div>
            <div className="nav-dropdown">
              <Link to="/template-left-sidebar" className={`nav-link ${isActive('/template-left-sidebar') || isActive('/template-right-sidebar') || isActive('/template-full-width') || isActive('/template-no-sidebar') || isActive('/template-grid-layout') ? 'active' : ''}`} onClick={closeMobileMenu}>TEMPLATE</Link>
          <div className="dropdown-menu">
            <Link to="/template-left-sidebar" className="dropdown-item">TEMPLATE LEFT SIDEBAR</Link>
            <Link to="/template-right-sidebar" className="dropdown-item">TEMPLATE RIGHT SIDEBAR</Link>
            <Link to="/template-full-width" className="dropdown-item">TEMPLATE FULL WIDTH</Link>
            <Link to="/template-no-sidebar" className="dropdown-item">TEMPLATE NO SIDEBAR</Link>
            <Link to="/template-grid-layout" className="dropdown-item">TEMPLATE GRID LAYOUT</Link>
          </div>
            </div>
          </nav>
          <button 
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </header>
  );
}

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const totalSlides = 3;

  const changeSlide = (direction) => {
    setCurrentSlide(prev => {
      if (direction === 1) {
        return prev === totalSlides - 1 ? 0 : prev + 1;
      } else {
        return prev === 0 ? totalSlides - 1 : prev - 1;
      }
    });
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Update active slide class
  useEffect(() => {
    const slides = document.querySelectorAll('.carousel-slide');
    const indicators = document.querySelectorAll('.indicator');
    
    slides.forEach((slide, index) => {
      slide.classList.toggle('active', index === currentSlide);
    });
    
    indicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === currentSlide);
    });
  }, [currentSlide]);

  // Scroll listener to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'coaches', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <Router>
      <GlobalHeader />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/template-left-sidebar" element={<TemplateLeftSidebar />} />
          <Route path="/template-right-sidebar" element={<TemplateRightSidebar />} />
          <Route path="/template-full-width" element={<TemplateFullWidth />} />
          <Route path="/template-no-sidebar" element={<TemplateNoSidebar />} />
          <Route path="/template-grid-layout" element={<TemplateGridLayout />} />
        </Routes>
    </Router>
  );
}

function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 3;

  const changeSlide = (direction) => {
    setCurrentSlide(prev => {
      if (direction === 1) {
        return prev === totalSlides - 1 ? 0 : prev + 1;
      } else {
        return prev === 0 ? totalSlides - 1 : prev - 1;
      }
    });
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Update active slide class
  useEffect(() => {
    const slides = document.querySelectorAll('.carousel-slide');
    const indicators = document.querySelectorAll('.indicator');
    
    slides.forEach((slide, index) => {
      slide.classList.toggle('active', index === currentSlide);
    });
    
    indicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === currentSlide);
    });
  }, [currentSlide]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-carousel">
          <div className="carousel-slide active" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1920&h=1080&fit=crop&crop=center)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>
            <div className="hero-overlay"></div>
          </div>
          <div className="carousel-slide" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1920&h=1080&fit=crop&crop=center)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>
            <div className="hero-overlay"></div>
          </div>
          <div className="carousel-slide" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1920&h=1080&fit=crop&crop=center)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>
            <div className="hero-overlay"></div>
          </div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">BECOME A PART OF SPORTS TEAM COMMUNITY</h1>
            <p className="hero-subtitle">Connect, Compete, Celebrate</p>
            <p className="hero-description">
              Join our comprehensive sports community where athletes connect, compete, and celebrate their achievements. 
              We provide world-class training, coaching, and development programs for athletes of all skill levels.
            </p>
          </div>
        </div>
        <div className="hero-navigation">
          <button className="nav-arrow left" onClick={() => changeSlide(-1)}>‹</button>
          <button className="nav-arrow right" onClick={() => changeSlide(1)}>›</button>
        </div>
        <div className="carousel-indicators">
          <span className="indicator active" onClick={() => goToSlide(0)}></span>
          <span className="indicator" onClick={() => goToSlide(1)}></span>
          <span className="indicator" onClick={() => goToSlide(2)}></span>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="two-column-layout">
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop&crop=center" 
                alt="Sports players in action" 
                className="sports-image"
              />
            </div>
            <div className="about-content">
              <h2 className="section-title">
                About <span className="accent-text">Sports Alliance</span>
              </h2>
              <div className="title-underline"></div>
              <p className="about-description">
                We are a leading sports alliance dedicated to nurturing athletic talent and building strong communities. 
                Our mission is to provide comprehensive training programs that help athletes reach their full potential.
              </p>
              <p className="about-description">
                Our experienced coaches and state-of-the-art facilities create the perfect environment for athletic development. 
                We focus on building character, discipline, and excellence in every athlete we train.
              </p>
              <div className="progress-bars">
                <div className="progress-item">
                  <div className="progress-label">FITNESS <span className="percentage">(95%)</span></div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '95%'}}></div>
                  </div>
                </div>
                <div className="progress-item">
                  <div className="progress-label">GAME PLAN <span className="percentage">(65%)</span></div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '65%'}}></div>
                  </div>
                </div>
                <div className="progress-item">
                  <div className="progress-label">STRATEGY <span className="percentage">(75%)</span></div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '75%'}}></div>
                  </div>
                </div>
                <div className="progress-item">
                  <div className="progress-label">PASSION <span className="percentage">(85%)</span></div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '85%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Games Section */}
      <section className="games-section">
        <div className="container">
          <h2 className="section-title">
            Our <span className="accent-text">Games</span>
          </h2>
          <div className="title-underline"></div>
          <p className="section-description">
            Professional training and coaching for various sports disciplines with expert guidance.
          </p>
          <div className="games-grid-all">
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&h=250&fit=crop&crop=center" alt="Badminton" className="game-image" />
              <h3 className="game-title">Badminton</h3>
              <p className="game-description">Professional badminton training with certified coaches and world-class facilities.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=250&fit=crop&crop=center" alt="Table Tennis" className="game-image" />
              <h3 className="game-title">Table Tennis</h3>
              <p className="game-description">Expert table tennis coaching and training programs for all skill levels.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?w=400&h=250&fit=crop&crop=center" alt="Tennis" className="game-image" />
              <h3 className="game-title">Tennis</h3>
              <p className="game-description">Professional tennis training and development programs with experienced coaches.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop&crop=center" alt="Athletics" className="game-image" />
              <h3 className="game-title">Athletics</h3>
              <p className="game-description">Track and field training with specialized coaching and modern facilities.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=400&h=250&fit=crop&crop=center" alt="Boxing" className="game-image" />
              <h3 className="game-title">Boxing</h3>
              <p className="game-description">Professional boxing training and fitness programs with expert trainers.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&h=250&fit=crop&crop=center" alt="Weightlifting" className="game-image" />
              <h3 className="game-title">Weightlifting</h3>
              <p className="game-description">Strength training and weightlifting coaching for all levels.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&h=250&fit=crop&crop=center" alt="Powerlifting" className="game-image" />
              <h3 className="game-title">Powerlifting</h3>
              <p className="game-description">Powerlifting training and competition preparation with certified coaches.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=250&fit=crop&crop=center" alt="Basketball" className="game-image" />
              <h3 className="game-title">Basketball</h3>
              <p className="game-description">Basketball training and team development programs for all ages.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&h=250&fit=crop&crop=center" alt="Volleyball" className="game-image" />
              <h3 className="game-title">Volleyball</h3>
              <p className="game-description">Volleyball coaching and team training programs with professional guidance.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=400&h=250&fit=crop&crop=center" alt="Cricket Women" className="game-image" />
              <h3 className="game-title">Cricket (Women)</h3>
              <p className="game-description">Women's cricket training and development programs with expert coaching.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
            <div className="game-card">
              <img src="https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=400&h=250&fit=crop&crop=center" alt="Shooting" className="game-image" />
              <h3 className="game-title">Shooting</h3>
              <p className="game-description">Professional shooting training and precision coaching with modern equipment.</p>
              <button className="game-btn">LEARN MORE</button>
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas Section */}
      <section className="focus-section">
        <div className="container">
          <h2 className="section-title">
            Our <span className="accent-text">Focus Areas</span>
          </h2>
          <div className="title-underline"></div>
          <p className="section-description">
            Specialized programs designed to enhance performance and skill development.
          </p>
          <div className="focus-grid">
            <div className="focus-card">
              <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=200&h=150&fit=crop&crop=center" alt="Testing and Evaluation" className="focus-image" />
              <h3 className="focus-title">Tests</h3>
              <p className="focus-description">
                Comprehensive testing and evaluation programs to assess performance and progress.
              </p>
              <button className="focus-btn">LEARN MORE</button>
            </div>
            <div className="focus-card">
              <img src="https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=200&h=150&fit=crop&crop=center" alt="Leadership Training" className="focus-image" />
              <h3 className="focus-title">Lead Skill</h3>
              <p className="focus-description">
                Leadership development and skill enhancement programs for athletes.
              </p>
              <button className="focus-btn">LEARN MORE</button>
            </div>
            <div className="focus-card">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=200&h=150&fit=crop&crop=center" alt="Online Learning" className="focus-image" />
              <h3 className="focus-title">Online Classes</h3>
              <p className="focus-description">
                Pay-per-class online training sessions with expert coaches and instructors.
              </p>
              <button className="focus-btn">LEARN MORE</button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Coaches Section */}
      <section id="coaches" className="coaches-section">
        <div className="container">
          <h2 className="section-title">
            Our <span className="accent-text">Coaches</span>
          </h2>
          <div className="title-underline"></div>
          <p className="section-description">
            Our professional coaching staff brings years of experience and expertise to help athletes excel. 
            We believe in personalized training approaches that cater to individual strengths and goals.
          </p>
          <div className="coaches-grid">
            <div className="coach-card">
              <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=120&h=120&fit=crop&crop=face" alt="Maria Doe" className="coach-image" />
              <h4 className="coach-name">Maria Doe</h4>
              <p className="coach-role">Trainer</p>
            </div>
            <div className="coach-card">
              <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=120&h=120&fit=crop&crop=face" alt="Elena Rodriguez" className="coach-image" />
              <h4 className="coach-name">Elena Rodriguez</h4>
              <p className="coach-role">Coach</p>
            </div>
            <div className="coach-card">
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop&crop=face" alt="Sarah" className="coach-image" />
              <h4 className="coach-name">Sarah</h4>
              <p className="coach-role">Coach</p>
            </div>
            <div className="coach-card">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&crop=face" alt="John Doe" className="coach-image" />
              <h4 className="coach-name">John Doe</h4>
              <p className="coach-role">Coach</p>
            </div>
            <div className="coach-card">
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&h=120&fit=crop&crop=face" alt="Markes" className="coach-image" />
              <h4 className="coach-name">Markes</h4>
              <p className="coach-role">Coach</p>
            </div>
          </div>
          <div className="coaches-navigation">
            <button className="nav-arrow">‹</button>
            <button className="nav-arrow">›</button>
          </div>
        </div>
      </section>

      {/* Our Valuable Clients Section */}
      <section className="clients-section">
        <div className="container">
          <h2 className="section-title">
            Our <span className="accent-text">Valuable Clients</span>
          </h2>
          <div className="title-underline"></div>
          <p className="section-description">
            We are proud to work with prestigious institutions and organizations across various sectors, 
            delivering excellence in sports infrastructure and development.
          </p>
          <div className="clients-grid">
            <div className="client-logo">
              <div className="client-name">Jayshree Periwal International School</div>
              <div className="client-tagline">Innovating minds... shaping future</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Jankidevi Public School</div>
              <div className="client-tagline">Excellence in Education</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Lakshmipat University</div>
              <div className="client-tagline">Higher Education Excellence</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Unique Dream Builders</div>
              <div className="client-tagline">Expect More</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Arihant Group</div>
              <div className="client-tagline">Shaping the future</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Warren Academy</div>
              <div className="client-tagline">Academic Excellence</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Shanti Asiatic School</div>
              <div className="client-tagline">Quality Education</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Manipal University Jaipur</div>
              <div className="client-tagline">Premier Education</div>
            </div>
            <div className="client-logo">
              <div className="client-name">SRN International School</div>
              <div className="client-tagline">Global Standards</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Samriddhi Builders</div>
              <div className="client-tagline">Enriching lives</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Olive Homes Real Estate</div>
              <div className="client-tagline">Quality Living</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Indian Railways</div>
              <div className="client-tagline">National Infrastructure</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Ministry of Defence</div>
              <div className="client-tagline">National Security</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Mody School</div>
              <div className="client-tagline">A Premier Girls' School</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Mody University</div>
              <div className="client-tagline">Excellence in Education</div>
            </div>
            <div className="client-logo">
              <div className="client-name">University of Rajasthan</div>
              <div className="client-tagline">Established 1947</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Bardiya Group</div>
              <div className="client-tagline">Business Excellence</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Rajasthan State Road Dev. Corp.</div>
              <div className="client-tagline">Infrastructure Development</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Maharishi Arvind University</div>
              <div className="client-tagline">Higher Learning</div>
            </div>
            <div className="client-logo">
              <div className="client-name">Poornima University</div>
              <div className="client-tagline">Technical Excellence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Results Section */}
      <section className="results-section">
        <div className="container">
          <h2 className="section-title">
            Latest <span className="accent-text">Results</span>
          </h2>
          <div className="title-underline"></div>
          <div className="results-grid">
            <div className="result-card">
              <div className="result-header">MON, OCT 21, YOUTH LEAGUE</div>
              <div className="teams-container">
                <div className="team">
                  <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=40&h=40&fit=crop&crop=center" alt="Turkey Team" className="team-logo" />
                  <div className="team-score">3</div>
                  <div className="team-name">Turkey</div>
                </div>
                <div className="vs">VS</div>
                <div className="team">
                  <img src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=40&h=40&fit=crop&crop=center" alt="NFL Team" className="team-logo" />
                  <div className="team-score">2</div>
                  <div className="team-name">NFL</div>
                </div>
              </div>
              <button className="result-btn">GAME DETAILS</button>
            </div>
            <div className="result-card">
              <div className="result-header">MON, OCT 21, YOUTH LEAGUE</div>
              <div className="teams-container">
                <div className="team">
                  <img src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=40&h=40&fit=crop&crop=center" alt="NOL Team" className="team-logo" />
                  <div className="team-score">1</div>
                  <div className="team-name">NOL</div>
                </div>
                <div className="vs">VS</div>
                <div className="team">
                  <img src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=40&h=40&fit=crop&crop=center" alt="NFL Team" className="team-logo" />
                  <div className="team-score">0</div>
                  <div className="team-name">NFL</div>
                </div>
              </div>
              <button className="result-btn">GAME DETAILS</button>
            </div>
          </div>
        </div>
      </section>

      {/* Sport News Section */}
      <section className="news-section">
        <div className="container">
          <h2 className="section-title">
            Sport <span className="accent-text">News</span>
          </h2>
          <div className="title-underline"></div>
          <p className="section-description">
            Stay updated with the latest sports news, training tips, and community events. 
            Our news section keeps you informed about everything happening in the sports world.
          </p>
          <div className="news-grid">
            <div className="news-card">
              <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop&crop=center" alt="Sports News" className="news-image" />
              <div className="news-date">15 Feb</div>
              <div className="news-content">
                <h3 className="news-title">NEW TRAINING PROGRAM LAUNCHED</h3>
                <p className="news-excerpt">
                  Advanced coaching techniques and personalized training programs for professional athletes
                </p>
                <button className="news-read-more">READ MORE →</button>
              </div>
            </div>
            <div className="news-card">
              <img src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=300&h=200&fit=crop&crop=center" alt="Sports News" className="news-image" />
              <div className="news-date">04 Jan</div>
              <div className="news-content">
                <h3 className="news-title">NULLAM PLACERAT MASSA ET</h3>
                <p className="news-excerpt">
                  Professional athletes share their training secrets and success strategies
                </p>
                <button className="news-read-more">READ MORE →</button>
              </div>
            </div>
            <div className="news-card">
              <img src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=300&h=200&fit=crop&crop=center" alt="Sports News" className="news-image" />
              <div className="news-date">11 Dec</div>
              <div className="news-content">
                <h3 className="news-title">PHASELLUS NEC METUS SCELERISQUE</h3>
                <p className="news-excerpt">
                  Professional athletes share their training secrets and success strategies
                </p>
                <button className="news-read-more">READ MORE →</button>
              </div>
            </div>
            <div className="news-card">
              <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop&crop=center" alt="Sports News" className="news-image" />
              <div className="news-date">11 Dec</div>
              <div className="news-content">
                <h3 className="news-title">CHAMPIONSHIP RESULTS ANNOUNCED</h3>
                <p className="news-excerpt">
                  Outstanding performances and record-breaking achievements in the latest tournament
                </p>
                <button className="news-read-more">READ MORE →</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="footer-background"></div>
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h3 className="footer-title">About Us</h3>
              <p className="footer-text">
                We are committed to excellence in sports training and community building. 
                Our comprehensive programs help athletes develop both physically and mentally.
              </p>
            </div>
            <div className="footer-column">
              <h3 className="footer-title">Latest Posts</h3>
              <div className="footer-post">
                <div className="post-date">
                  <span className="day">15</span>
                  <span className="month">Fri</span>
                </div>
                <div className="post-content">
                  <h4 className="post-title">New Training Program Launched</h4>
                  <p className="post-excerpt">Advanced coaching techniques for professional athletes</p>
                </div>
              </div>
              <div className="footer-post">
                <div className="post-date">
                  <span className="day">04</span>
                  <span className="month">Fri</span>
                </div>
                <div className="post-content">
                  <h4 className="post-title">Championship Results Announced</h4>
                  <p className="post-excerpt">Outstanding performances in the latest tournament</p>
                </div>
              </div>
              <a href="#" className="view-more">View More...</a>
            </div>
            <div className="footer-column">
              <h3 className="footer-title">Quick Links</h3>
              <ul className="footer-links">
                <li><a href="#" className="footer-link active">HOME</a></li>
                <li><a href="#" className="footer-link">ABOUT OUR WEBSITE</a></li>
                <li><a href="#" className="footer-link">SHOP</a></li>
                <li><a href="#" className="footer-link">GALLERY</a></li>
                <li><a href="#" className="footer-link">CONTACT</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h3 className="footer-title">Contact Details</h3>
              <div className="contact-info">
                <div className="contact-item">📞 +91-98294 49492</div>
                <div className="contact-item">📞 +91-98870 49492</div>
                <div className="contact-item">✉️ rkaggarwaljpr@gmail.com</div>
                <div className="contact-item">📍 Regd. Office: B-7, Ganesh Nagar, New Sanganer Road, Sodala, Jaipur-302019 (Raj.) INDIA</div>
                <div className="contact-item">📍 Corp. Office: Plot No. 3/3 A, Surya Nagar, Gopalpura Byepass, Jaipur-302019 (Raj.) INDIA</div>
                <div className="contact-item">📍 Factory Address: 15 Dr. Rajender Prasad Nagar-B, Jaipur-302020 (Raj.) INDIA</div>
              </div>
            </div>
          </div>
          <div className="footer-logo">
            <img src="/assets/main logo.png" alt="Elite International Sports Logo" className="footer-logo-icon" />
            <span className="footer-logo-text">ELITE INTERNATIONAL SPORTS</span>
          </div>
          <div className="footer-social">
            <span className="social-icon">f</span>
            <span className="social-icon">t</span>
            <span className="social-icon">in</span>
            <span className="social-icon">G+</span>
          </div>
          <div className="footer-copyright">
            Copyright © 2023 Elite International Sports. Building Future, Shaping Champions.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
